/**
 * Utility functions for extracting and formatting error information from various error formats.
 * Used across components that need to display user-friendly error messages.
 */

import { extractErrorInfo } from "@storytell/chat-ui/util";
import { serverMessage } from "@storytell/client";
import type { useI18n } from "~/i18n/context";
import type { TranslationKey } from "~/i18n/translations";

// The extractor moved to @storytell/chat-ui (chat-tools port) so the shared
// confirm-action renderers read errors the same way in both apps.
export { extractErrorInfo };

/**
 * Gets a user-friendly error message for asset operations (copy/move).
 * Handles common error codes (404, 403) and provides localized error messages.
 *
 * @param error - The error to format
 * @param projectName - Optional project name to include in the error message
 * @param t - Translation function for i18n (accepts any string key)
 * @param errorKeys - Object containing translation keys for different error types
 * @returns A user-friendly error message string
 */
export function getUserFriendlyAssetErrorMessage(
  error: unknown,
  projectName: string | undefined,
  // oxlint-disable-next-line typescript/no-explicit-any -- Translation function accepts any key string
  t: (key: any, params?: Record<string, string>) => string,
  errorKeys: {
    permissionDenied: string;
    permissionDeniedWithProject: string;
    generic: string;
  }
): string {
  const errorInfo = extractErrorInfo(error);
  const code = errorInfo.code;
  const message = errorInfo.message?.toLowerCase() || "";

  if (code === 404 || code === "404" || message.includes("not found") || message.includes("404")) {
    return projectName ? t(errorKeys.permissionDeniedWithProject, { projectName }) : t(errorKeys.permissionDenied);
  }

  if (code === 403 || code === "403" || message.includes("permission") || message.includes("access denied")) {
    return projectName ? t(errorKeys.permissionDeniedWithProject, { projectName }) : t(errorKeys.permissionDenied);
  }

  return errorInfo.message || t(errorKeys.generic);
}

export type IntegrationPrefix =
  | "google_workspace"
  | "slack_integration"
  | "notion_integration"
  | "linear_integration"
  | "github_integration"
  | "mcp_integration"
  | "organization.ai_providers";

// The i18n checker only counts keys it can read verbatim, and only a literal
// satisfies TranslationKey.
const STATUS_KEYS: Record<
  IntegrationPrefix,
  {
    // Absent on a feature no plan gates, where a 403 only ever means the caller
    // isn't permitted.
    upgradeRequired?: TranslationKey;
    permissionDenied: TranslationKey;
    serviceUnavailable: TranslationKey;
    generic: TranslationKey;
  }
> = {
  google_workspace: {
    upgradeRequired: "google_workspace.error_upgrade_required",
    permissionDenied: "google_workspace.error_permission_denied",
    serviceUnavailable: "google_workspace.error_service_unavailable",
    generic: "google_workspace.error_generic",
  },
  slack_integration: {
    upgradeRequired: "slack_integration.error_upgrade_required",
    permissionDenied: "slack_integration.error_permission_denied",
    serviceUnavailable: "slack_integration.error_service_unavailable",
    generic: "slack_integration.error_generic",
  },
  notion_integration: {
    upgradeRequired: "notion_integration.error_upgrade_required",
    permissionDenied: "notion_integration.error_permission_denied",
    serviceUnavailable: "notion_integration.error_service_unavailable",
    generic: "notion_integration.error_generic",
  },
  linear_integration: {
    upgradeRequired: "linear_integration.error_upgrade_required",
    permissionDenied: "linear_integration.error_permission_denied",
    serviceUnavailable: "linear_integration.error_service_unavailable",
    generic: "linear_integration.error_generic",
  },
  github_integration: {
    upgradeRequired: "github_integration.error_upgrade_required",
    permissionDenied: "github_integration.error_permission_denied",
    serviceUnavailable: "github_integration.error_service_unavailable",
    generic: "github_integration.error_generic",
  },
  mcp_integration: {
    upgradeRequired: "mcp_integration.error_upgrade_required",
    permissionDenied: "mcp_integration.error_permission_denied",
    serviceUnavailable: "mcp_integration.error_service_unavailable",
    generic: "mcp_integration.error_generic",
  },
  "organization.ai_providers": {
    permissionDenied: "organization.ai_providers.error_permission_denied",
    serviceUnavailable: "organization.ai_providers.error_service_unavailable",
    generic: "organization.ai_providers.error_generic",
  },
};

/**
 * Localizes an integration error: tries the backend's string `code` first, then
 * falls back to status-based keys under the `prefix` namespace.
 */
export function translateIntegrationError(
  err: unknown,
  t: ReturnType<typeof useI18n>["t"],
  prefix: IntegrationPrefix
): string {
  const info = extractErrorInfo(err);
  const keys = STATUS_KEYS[prefix];
  if (typeof info.code === "string") {
    const translated = t(info.code as TranslationKey);
    if (translated && translated !== info.code) return translated;
  }
  if (info.code === 403) return t(keys.upgradeRequired ?? keys.permissionDenied);
  if (info.code === 404 || info.code === 401) return t(keys.permissionDenied);
  if (typeof info.code === "number" && info.code >= 500) return t(keys.serviceUnavailable);
  return info.message === undefined ? t(keys.generic) : serverMessage(info.message);
}

/**
 * The message in a JSON error body from one of the routes/api proxies, which forward the backend
 * envelope's `humane` or answer with their own `error` or `message`. Undefined when the body carries
 * none of them as a non-empty string.
 */
export function proxyErrorMessage(body: unknown): string | undefined {
  if (typeof body !== "object" || body === null) return undefined;
  for (const key of ["humane", "error", "message"]) {
    const value: unknown = Reflect.get(body, key);
    if (typeof value === "string" && value) return value;
  }
  return undefined;
}
